import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const AdminLogin = () => {
  if (typeof document !== "undefined") document.title = "Admin sign in — School Smiles Australia";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/enquiries";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-2xl px-5 py-3 shadow-sm border border-[#E5F0FA]">
            <Logo />
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(20,50,74,0.06)] border border-[#E5F0FA] p-8 md:p-10">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-[#14324A]">Admin sign in</h1>
          <p className="mt-2 text-sm text-[#4B6B86]">Sign in to view enquiries submitted through the website.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-sm font-medium text-[#14324A]" htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#14324A]" htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="mt-2"
              />
            </div>

            {error && (
              <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center"
              data-testid="admin-login-submit"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} strokeWidth={2.5} />}
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link to="/admin/forgot-password" className="text-[#1677C8] hover:text-[#14324A] font-medium" data-testid="admin-login-forgot">
              Forgot password?
            </Link>
            <Link to="/" className="text-[#4B6B86] hover:text-[#14324A]">Back to site</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
